import React, { useState, useRef, useCallback, useEffect } from 'react'
import DropdownRB from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'

import { ButtonVariant } from '../../interfaces'
import { Item } from './interfaces'

interface Props {
  /** Determines the dropdown toggle text */
  text: string
  /** Determines the dropdown's items */
  items: Item[]
  /** Determines the dropdown toggle button id */
  id: string
  /** Determines the dropdown toggle variant color */
  variant: ButtonVariant
  /** Determines the horizontal alignment of the dropdown items */
  alignRight?: boolean
  /** Determines the dropdown toggle button size */
  size?: 'sm' | 'md' | 'lg'
  /** Determines the dropdown's direction */
  direction?: 'down' | 'up' | 'left' | 'right'
  /** Determines the dropdown's custom style */
  style?: Record<string, any>
}

/**
 * Customizable dropdown component based on React-Bootstrap dropdown
 * Supports keyboard navigation: Up/Down arrows, Enter to select, Escape to close
 */
const Dropdown = (props: Props) => {
  const { text, size, id, items, direction, variant, style, alignRight } = props
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Scroll the highlighted dropdown item into view
  const scrollHighlightedIntoView = useCallback((index: number) => {
    if (index < 0) return
    requestAnimationFrame(() => {
      const menuNode = dropdownRef.current?.querySelector('.dropdown-menu')
      if (!menuNode) return
      const menuItems = menuNode.querySelectorAll('.dropdown-item')
      const targetItem = menuItems[index] as HTMLElement
      if (targetItem) {
        targetItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    })
  }, [])

  // Scroll to highlighted item whenever it changes
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0) {
      scrollHighlightedIntoView(highlightedIndex)
    }
  }, [highlightedIndex, isOpen, scrollHighlightedIntoView])

  // Reset highlighted index when dropdown opens/closes
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0)
    } else {
      setHighlightedIndex(-1)
    }
  }, [isOpen])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      // Open dropdown on ArrowDown or Enter when closed
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    const maxIndex = items.length - 1

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault() // Prevent page scrolling
        // Boundary: stay at last item
        setHighlightedIndex((prev) => Math.min(prev + 1, maxIndex))
        break
      case 'ArrowUp':
        e.preventDefault() // Prevent page scrolling
        // Boundary: stay at first item
        setHighlightedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex <= maxIndex) {
          const item = items[highlightedIndex]
          if (item.onClick) {
            item.onClick(e as any)
          }
          setIsOpen(false)
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        // Return focus to the toggle button
        const toggleBtn = dropdownRef.current?.querySelector('button')
        toggleBtn?.focus()
        break
      default:
        break
    }
  }, [isOpen, highlightedIndex, items])

  const getDropdownItem = (item: Item, i: number) => (
    <DropdownRB.Item
      style={item.style}
      key={item.key || `dropdown-item-${i.toString()}`}
      eventKey={item.eventKey || `dropdown-event-${i.toString()}`}
      onClick={item.onClick}
      active={i === highlightedIndex}
      className={i === highlightedIndex ? 'dropdown-item-highlighted' : ''}
    >
      {item.text}
    </DropdownRB.Item>
  )

  return (
    <div ref={dropdownRef} onKeyDown={handleKeyDown}>
      <DropdownButton
        drop={direction}
        size={size}
        variant={variant}
        title={text}
        id={id}
        style={style}
        alignRight={alignRight}
        show={isOpen}
        onToggle={(nextShow) => setIsOpen(nextShow)}
      >
        {items.map((item, i) => getDropdownItem(item, i))}
      </DropdownButton>
    </div>
  )
}
Dropdown.defaultProps = {
  id: `dropdown${Math.floor(Math.random() * 10000)}`,
  variant: 'light',
  size: 'sm',
  direction: 'down',
}

export { Dropdown }
